import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 32000-32999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const groupsErrors = {
  // 32xxx is the groups module's series. Two sites reused SPACE_NOT_FOUND (7015) and
  // PROJECT_NOT_FOUND verbatim; two stay generic. 32009-32012 are FALLBACK codes: each site forwards
  // a child service's own code when it has one and falls back to these, so each keeps its message.
  // 32001-32012 — the groups module's split.
  GROUP_NOT_REMOVED_FROM_ANY_PROJECT: {
    errorCode: 32001,
    errorMessage: t("The group was not removed from any project."),
    isDynamic: false,
  },
  GROUP_NOT_FOUND: {
    errorCode: 32002,
    errorMessage: t("No such group exists"),
    isDynamic: false,
  },
  GROUP_DELETE_FAILED: {
    errorCode: 32003,
    errorMessage: t("Error deleting group"),
    isDynamic: false,
  },
  // Deliberately undifferentiated: the Space is missing OR has no project attached.
  GROUP_SPACE_HAS_NO_PROJECT: {
    errorCode: 32004,
    errorMessage: t("Space not found or has no associated project."),
    isDynamic: false,
  },
  GROUP_FETCH_FAILED: {
    errorCode: 32005,
    errorMessage: t("Error fetching groups from the collection."),
    isDynamic: false,
  },
  GROUP_USERS_FETCH_FAILED: {
    errorCode: 32006,
    errorMessage: t("Error fetching users from the collection."),
    isDynamic: false,
  },
  GROUP_INSUFFICIENT_DATA_TO_SAVE: {
    errorCode: 32007,
    errorMessage: t("Insufficient data to save group."),
    isDynamic: false,
  },
  GROUP_USER_ACCOUNT_ID_REQUIRED: {
    errorCode: 32008,
    errorMessage: t("Every selected user must carry an account id."),
    isDynamic: false,
  },
  GROUP_PROJECT_USERS_ADD_FAILED: {
    errorCode: 32011,
    errorMessage: t("The selected user(s) could not be added to the project."),
    isDynamic: false,
  },
  GROUP_PROJECT_GROUP_USERS_ADD_FAILED: {
    errorCode: 32012,
    errorMessage: t("The group's users could not be added to the project."),
    isDynamic: false,
  },
  GROUP_NO_USERS_ADDED: {
    errorCode: 32013,
    errorMessage: t("No users were added to the group."),
    isDynamic: false,
  },
  GROUP_UNCHANGED: {
    errorCode: 32014,
    errorMessage: t("The group was not modified."),
    isDynamic: false,
  },
  // 32015-32019 came off VALIDATION_FAILED (9004).
  GROUP_PROJECT_OR_SPACE_ID_REQUIRED: {
    errorCode: 32015,
    errorMessage: t("projectId or spaceId is required."),
    isDynamic: false,
  },
  GROUP_ID_REQUIRED: {
    errorCode: 32016,
    errorMessage: t("Group id is required."),
    isDynamic: false,
  },
  GROUP_USER_LIST_REQUIRED: {
    errorCode: 32017,
    errorMessage: t("A list of users to add is required."),
    isDynamic: false,
  },
  GROUP_SPACE_OR_PROJECT_ID_REQUIRED: {
    errorCode: 32018,
    errorMessage: t("spaceId or projectId is required."),
    isDynamic: false,
  },
  GROUP_PROJECT_AND_GROUP_REQUIRED: {
    errorCode: 32019,
    errorMessage: t("A project and a group are required to add a group's users to a Space."),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
